import React, { useRef } from 'react';
import { BlockRenderer } from '../../renderer';
import { getNumberedListIndex, indentStyle } from '../../utils/indentUtils';
import { createBlock, flattenBlocks } from '../../utils/blockUtils';
import { focusEditable } from '../../utils/focusUtils';
import { EDITABLE_TYPES } from '../../utils/editorConstants';
import IconPicker from '../../../SprintDetail/shared/IconPicker';
import { renderIcon } from '../../../SprintDetail/FlowchartView/utils/renderIcon';
import { CalloutMenu } from './components/CalloutMenu';
import { CalloutToolbar } from './components/CalloutToolbar';
import { useCalloutColumns } from './hooks/useCalloutColumns';
import { useCalloutMenu } from './hooks/useCalloutMenu';
import {
  CALLOUT_PALETTE,
  calloutSurfaceStyle,
  colorToRgb,
  getCalloutIconAccent,
  resolveCalloutSurface,
} from './utils/calloutTheme';

export {
  CALLOUT_COLORS,
  CALLOUT_PALETTE,
  CALLOUT_SURFACES,
  NEUTRAL_CALLOUT_BORDER,
  NEUTRAL_CALLOUT_FILL,
  getCalloutIconAccent,
  parseHexColor,
} from './utils/calloutTheme';

/**
 * CalloutBlock — a highlighted box for tips and warnings that holds other
 * blocks as children. Clicking its background always gives you somewhere to
 * type: an empty callout grows a fresh paragraph, a filled one focuses its
 * last editable block.
 *
 * Hover on a filled callout shows Copy plus a columns chip — click it, then
 * 1–6, to turn on that many columns (1 unwraps). While columns are on, the
 * editor keeps packing children so the columns stay even as content changes.
 *
 * Customizing (color + icon): a context menu opens by RIGHT-CLICKING the
 * callout's own background (outside any child block) or its drag handle.
 * Left-clicking the handle still selects, dragging still moves. Colors are
 * the CALLOUT_COLORS tints; the icon uses the app's shared IconPicker (same
 * emojis/FontAwesome/custom uploads as mission and campaign icons) rendered
 * via the shared renderIcon.
 */
export const CalloutBlock = ({
  block,
  onUpdate,
  onUnwrapCallout,
  onWrapInCallout,
  resolveVariable,
  ...passThrough
}) => {
  // No default icon — empty callouts are iconless until the user picks one.
  // Quote variant still stores ❝ but hides the slot via CSS.
  const icon = block.props?.icon || null;
  const color = colorToRgb(block.props?.color) ? block.props.color : 'neutral';
  const rgb = colorToRgb(color) || CALLOUT_PALETTE.neutral;
  // 'fill' | 'border' | 'both' | 'none'
  const surface = resolveCalloutSurface(block.props);
  const isNone = surface === 'none';
  const isEmpty = !block.children || block.children.length === 0;
  const isQuote = block.props?.variant === 'quote';
  const isNeutral = color === 'neutral';
  const pointerDownRef = useRef(null);

  const {
    menuPos,
    setMenuPos,
    iconPickerAnchor,
    setIconPickerAnchor,
    hexDraft,
    setHexDraft,
    pickColor,
    pickSurface,
    applyHexDraft,
    openIconPicker,
    handleIconChange,
  } = useCalloutMenu({ block, onUpdate });

  const {
    copied,
    columnsOpen,
    setColumnsOpen,
    columnsRef,
    columnCount,
    applyColumns,
    handleCopy,
  } = useCalloutColumns({ block, onUpdate, resolveVariable });

  const isOwnBackground = (e) => {
    const clickedWrapper = e.target.closest('[data-block-id]');
    return clickedWrapper?.getAttribute('data-block-id') === block.id;
  };

  const handlePointerDown = (e) => {
    if (e.button !== 0) return;
    pointerDownRef.current = { x: e.clientX, y: e.clientY };
  };

  const handleClick = (e) => {
    // Only act on clicks on the callout background itself — clicks on child
    // blocks (whose wrappers sit deeper) are handled by those blocks.
    if (!isOwnBackground(e)) return;
    // A text-selection drag that ends on callout padding still fires click —
    // ignore it so we don't jump focus (and scroll) to the last block.
    if (pointerDownRef.current) {
      const dx = Math.abs(e.clientX - pointerDownRef.current.x);
      const dy = Math.abs(e.clientY - pointerDownRef.current.y);
      pointerDownRef.current = null;
      if (dx >= 5 || dy >= 5) return;
    }
    e.stopPropagation();

    if (isEmpty) {
      const child = createBlock('text');
      onUpdate(block.id, { children: [child] });
      focusEditable(child.id, { preventScroll: true });
    } else {
      const lastEditable = flattenBlocks(block.children)
        .reverse()
        .find((b) => EDITABLE_TYPES.includes(b.type));
      if (lastEditable) focusEditable(lastEditable.id, { preventScroll: true });
    }
  };

  // Right-click on the callout's own background opens the menu
  const handleContextMenu = (e) => {
    if (!isOwnBackground(e)) return;
    e.preventDefault();
    e.stopPropagation();
    setMenuPos({ x: e.clientX, y: e.clientY });
  };

  const iconAccent = getCalloutIconAccent(icon);
  const calloutStyle = {
    ...calloutSurfaceStyle({ isQuote, isNone, isNeutral, surface, rgb }),
    ...indentStyle(block),
    ...(iconAccent ? { '--callout-accent': iconAccent } : null),
  };

  return (
    <div
      className={`callout-block${isQuote ? ' callout-variant-quote' : ''}${!icon && !isQuote ? ' callout-block--no-icon' : ''}${iconAccent ? ' callout-block--icon-accent' : ''}${columnsOpen ? ' callout-block--toolbar-open' : ''}${!isQuote && isNone ? ' callout-block--none' : ''}`}
      style={calloutStyle}
      onMouseDown={handlePointerDown}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
    >
      {/* Copy is for filled callout boxes only — quotes read as plain text
          with a left bar, so a Copy chip there looks like every paragraph
          grew an affordance. Sticky rail pins the tag while scrolling tall
          callouts. */}
      {!isQuote && (
        <CalloutToolbar
          columnsRef={columnsRef}
          columnsOpen={columnsOpen}
          setColumnsOpen={setColumnsOpen}
          columnCount={columnCount}
          applyColumns={applyColumns}
          copied={copied}
          onCopy={handleCopy}
        />
      )}
      {icon ? (
        <span className="callout-icon">
          {renderIcon(icon, { size: '18px' })}
        </span>
      ) : null}
      <div className="callout-content">
        {block.children?.map((child, i) => (
          <BlockRenderer
            key={child.id}
            {...passThrough}
            resolveVariable={resolveVariable}
            block={child}
            onUpdate={onUpdate}
            onUnwrapCallout={onUnwrapCallout}
            onWrapInCallout={onWrapInCallout}
            prevType={block.children[i - 1]?.type}
            nextType={block.children[i + 1]?.type}
            listNumber={
              child.type === 'number'
                ? getNumberedListIndex(block.children, i)
                : undefined
            }
          />
        ))}
      </div>

      {menuPos && (
        <CalloutMenu
          blockId={block.id}
          menuPos={menuPos}
          onClose={() => setMenuPos(null)}
          surface={surface}
          color={color}
          rgb={rgb}
          isNeutral={isNeutral}
          hexDraft={hexDraft}
          setHexDraft={setHexDraft}
          pickSurface={pickSurface}
          pickColor={pickColor}
          applyHexDraft={applyHexDraft}
          openIconPicker={openIconPicker}
          icon={icon}
          isQuote={isQuote}
          handleIconChange={handleIconChange}
          onWrapInCallout={onWrapInCallout}
          onUnwrapCallout={onUnwrapCallout}
        />
      )}

      {iconPickerAnchor && (
        <IconPicker
          currentIcon={icon}
          onIconChange={handleIconChange}
          onIconDeleted={() => handleIconChange(null)}
          onClose={() => setIconPickerAnchor(null)}
          anchorRect={iconPickerAnchor}
        />
      )}
    </div>
  );
};
